import {
  Button,
  Chip,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getLanguageName } from "../utils/generalFunctions";
import Loader from "../Components/Loader";
import { getProjectById } from "../features/projects/api";
import QAReviewPanel from "../features/qaReview/QAReviewPanel";
import {
  deleteWithFirebaseAuth,
  postWithFirebaseAuth,
} from "../utils/firebaseApiClient";
import {
  AUDIO_CHOPPING,
  CREATE_ALL_VOCALS_AUTOMATICALLY,
  DELETE_PROJECT,
  END_TO_END_AUTOMATION,
  SYNC_LIPS,
  TRANSCRIBE,
  RUN_NONVERBAL_DETECTION,
  TRANSLATE,
  MAP_TRANSLATION,
  VOICE_TRAINING,
  VOICE_TRAINING_STATUS_CHECK,
  TRY_CLEAR_VOICES,
  GET_TASKS_STATUS,
  MERGING_FULL_API
} from "../ApiEndpoints";
import { ToastContainer, toast } from "react-toastify";
import DeleteConfirmationModal from "../Components/DeleteConfirmationModal";
import DialogueRatingSourceSelector from "../Components/DialogueRatingSourceSelector";
import SrtUploadButton from "../Components/ProjectSubtitleUpload/SrtUploadButton.js";

import { useContext } from "react";
import { Store } from "../Store";
import { secondaryColor } from "../utils/constants.js";
import { isVisibleVideoRole } from "../Data/tasksCode.js";
import { setStoredProjectFile } from "../utils/storage";
import {
  getAudioChoppingBlocker,
  loadAdminActionStatuses,
  TASK_STATUS,
} from "../features/adminActions/taskStatuses.js";
import TaskProgressCell from "../features/adminActions/TaskProgressCell.js";
import LangSelect from "../features/adminActions/LangSelect.js";
import TaskStepsCell from "../features/adminActions/TaskStepsCell.js";
import { getActionBlockers } from "../features/adminActions/taskPrerequisites.js";
import TaskFailureNotice from "../features/adminActions/TaskFailureNotice.js";
import { mixedStemWarning } from "../features/nonverbal/nonverbal.js";
import ModeCards from "../features/adminActions/ModeCards.js";
import ReloadVideoButton from "../features/adminActions/ReloadVideoButton.js";
import { useRemuxRequest } from "../features/adminActions/useRemuxRequest.js";
import {
  describeVideoIngestion,
  getVideoIngestionState,
  pendingVideoIngestion,
  VIDEO_INGESTION_STATE,
} from "../features/adminActions/videoIngestion.js";
import {
  CHOPPING_FAILED_ATTENTION,
  TAKE_FAILED_ATTENTION,
} from "../Components/DialogueSearchFilter/DialogueFilterBar";
import {
  CREATE_TAKES_MODE_OPTIONS,
  RETRANSLATE_MODE_OPTIONS,
  DEFAULT_STEM_SEPARATION_PROVIDER,
  STEM_SEPARATION_OPTIONS,
} from "../features/adminActions/modeOptions.js";

// Segmented toggle: one outlined control whose selected segment matches
// TaskButton (e.g. the transcriber page's Next button).
// How a task's output was produced is metadata, not state: a neutral tag
// keeps color for the status itself.
const methodTagSx = {
  ml: 1,
  height: 20,
  borderColor: "#BFBFBF",
  color: "text.secondary",
};

// Each emotion of each speaker that has audio samples is one voice; emotions
// without samples are not counted.
const VOICE_TRAINING_PROGRESS_LABELS = {
  alreadyDone: {
    label: "already trained",
    hint: "Voices that existed before this run; they are not retrained",
  },
  succeeded: {
    label: "trained",
    hint: "A voice was created in this run from audio samples or designed as a fallback",
  },
  notEnoughSamples: {
    label: "not enough samples",
    hint: "Samples existed, but there was too little usable audio to train a voice",
  },
  restricted: {
    label: "restricted",
    hint: "ElevenLabs refused to clone this voice, e.g. because it sounds like a protected public figure",
  },
  failed: {
    label: "failed",
    hint: "Samples existed, but training failed for another reason; see the voice training logs",
  },
};

// Runs from before voice training stopped counting emotions without samples
// reported them as skipped; that count is no longer shown.
const withoutSkipped = (progress) => progress && { ...progress, skipped: 0 };

// Each dialogue in the chosen language is one take.
const CREATE_TAKES_PROGRESS_LABELS = {
  alreadyDone: {
    label: "already had a take",
    hint: "Missing only keeps dialogues that already have generated audio",
  },
  succeeded: {
    label: "generated",
    hint: "A take was generated in this run",
  },
  failed: {
    label: "failed",
    hint: "Generation was attempted but failed; see the audio-gen service logs",
  },
  skipped: {
    label: "no voice",
    hint: "The speaker has no trained voice for the dialogue's emotion",
  },
};

// Each dialogue in each target language is one item.
const TRANSLATION_PROGRESS_LABELS = {
  translate: {
    alreadyDone: {
      label: "already translated",
      hint: "Missing only keeps dialogues that already have a translation",
    },
    succeeded: { label: "translated", hint: "Translated and saved in this run" },
    failed: {
      label: "failed",
      hint: "The dialogue was deleted while the job ran, so its translation was not saved",
    },
  },
  map: {
    succeeded: { label: "mapped from SRT", hint: "Matched to a line in the provided SRT" },
    failed: {
      label: "failed",
      hint: "The dialogue was deleted while the job ran, so its translation was not saved",
    },
    skipped: {
      label: "not in SRT, translated",
      hint: "No matching SRT line was found, so the model translated it instead",
    },
  },
};

// Transcription is one long ElevenLabs request, so it shows steps, not counts.
const NONVERBAL_DETECTION_STEP_LABELS = {
  load: "Loading audio and timings",
  model: "Listening for non-verbal sounds",
  decide: "Deciding what to add",
  save: "Saving regions",
};

const TRANSCRIPTION_STEP_LABELS = {
  transcribe: {
    prepare: "Preparing audio",
    elevenlabs: "Transcribing with ElevenLabs",
    timings: "Tightening timings",
    save: "Saving dialogues",
  },
  align: {
    prepare: "Preparing audio",
    align: "Aligning SRT with ElevenLabs",
    speakers: "Identifying speakers",
    save: "Saving dialogues",
  },
};

// Demucs and AudioShake both run as long jobs, so stem separation shows steps.
const STEM_SEPARATION_STEP_LABELS = {
  demucs: {
    extract: "Extracting audio",
    separate: "Separating with Demucs",
    combine: "Combining stems",
    save: "Saving stems",
  },
  audioshake: {
    submit: "Submitting to AudioShake",
    separate: "Separating with AudioShake",
    save: "Saving stems",
  },
};

const STEM_SEPARATION_STEP_COUNT_NOUNS = { separate: "chunks" };

const TRANSCRIPTION_STEP_COUNT_NOUNS = {
  timings: "windows",
  save: "dialogues",
};

const stemLabels = {
  dialogue: "Dialogue",
  music: "Music",
  effect: "Effects",
  musicEffects: "Music & effects",
};

const stemSourceLabels = {
  generated: "Generated",
  provided: "Provided",
};

const translationSourceLabels = {
  generated: "Generated",
  subtitle: "From SRT",
};

// Names, Actions, Status, Progress. Status gets room for its method chips.
const ACTIONS_TABLE_COLUMN_WIDTHS = ["12%", "32%", "24%", "32%"];

const translationModeToggleSx = {
  border: "1px solid #BFBFBF",
  borderRadius: "var(--radius-md)",
  overflow: "hidden",
  "& .MuiToggleButtonGroup-grouped": {
    border: "none",
    borderRadius: 0,
    margin: 0,
    px: "10px",
    py: "5px",
    textTransform: "none",
    fontFamily: "var(--font-body)",
    fontSize: "var(--font-size-normal)",
    lineHeight: "normal",
    whiteSpace: "nowrap",
    color: "text.primary",
    "&:not(:first-of-type)": {
      marginLeft: 0,
      borderLeft: "1px solid #BFBFBF",
    },
    "&.Mui-selected, &.Mui-selected:hover": {
      background: "var(--color-button-primary-bg)",
      color: "#f5f5f5",
    },
  },
};

// Disabled buttons emit no events; the span keeps the reason reachable.
const GatedButton = ({ blocker, disabled, children, ...props }) => (
  <Tooltip title={blocker || ""} placement="top" arrow>
    <span>
      <Button {...props} disabled={Boolean(blocker) || disabled}>
        {children}
      </Button>
    </span>
  </Tooltip>
);

const ActionsScreen = () => {
  const params = useParams();
  const { id: projectId } = params;
  // const { state } = useContext(Store);
  // const { accessToken } = state;
  const [dataLoader] = useState(false);
  const [projectFile, setProjectFile] = useState({});
  const [transcriptionStatus, setTranscriptionStatus] = useState("notStarted");
  const [nonverbalDetectionStatus, setNonverbalDetectionStatus] = useState("notStarted");
  const [checkAudioTrainingStatus, setcheckAudioTrainingStatus] =
    useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletedProjectName, setDeletedProjectName] = useState();
  const [deletedProjectId, setDeletedProjectId] = useState();

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;
  const [createTakesMode, setCreateTakesMode] = useState("soft");
  const [translationStatus, setTranslationStatus] = useState("notStarted");
  const [voiceTrainingStatus, setVoiceTrainingStatus] = useState("notStarted");
  const [voiceGenerationStatus, setVoiceGenerationStatus] = useState("notStarted");
  const [remuxStatus, setRemuxStatus] = useState("notStarted");
  const [audioChoppingStatus, setAudioChoppingStatus] = useState("notStarted");
  const [taskProgress, setTaskProgress] = useState({});
  const [taskFailures, setTaskFailures] = useState({});
  const [lipSyncStatus, setLipSyncStatus] = useState("notStarted");
  const [stemSeparationStatus, setStemSeparationStatus] = useState("notStarted");
  const [stemSeparationProvider, setStemSeparationProvider] = useState(
    DEFAULT_STEM_SEPARATION_PROVIDER
  );
  const [useOriginalAudioRating, setUseOriginalAudioRating] = useState(false);

  const [transLang, setTransLang] = useState("");
  const [mappingLang, setMappingLang] = useState("");
  const [translationMode, setTranslationMode] = useState("translate");
  const [retranslateMode, setRetranslateMode] = useState("soft");
  const [transcriptionMode, setTranscriptionMode] = useState("transcribe");
  const [transcriptionSrt, setTranscriptionSrt] = useState("");
  const [genLang, setGenLang] = useState("");
  const [syncLang, setSyncLang] = useState("");
  const [syncSubmitting, setSyncSubmitting] = useState(false);
  const [remuxLang, setRemuxLang] = useState("");

  // Normalize project creationDate into epoch milliseconds for reliable age checks.
  const getCreationDateMs = (creationDate) => {
    // Guard: return null when no creation date is available.
    if (!creationDate) {
      return null;
    }

    // Handle Firestore Timestamp-like objects with seconds/nanoseconds fields.
    if (typeof creationDate?.seconds === "number") {
      return creationDate.seconds * 1000 + Math.floor((creationDate.nanoseconds || 0) / 1000000);
    }

    // Handle native Date instances.
    if (creationDate instanceof Date) {
      return creationDate.getTime();
    }

    // Handle ISO date strings and numeric timestamps.
    if (typeof creationDate === "string" || typeof creationDate === "number") {
      // Parse the incoming value into a Date object.
      const parsedDate = new Date(creationDate);
      // Convert parsed date to milliseconds.
      const time = parsedDate.getTime();
      // Return null for invalid dates, otherwise return milliseconds.
      return Number.isNaN(time) ? null : time;
    }

    // Fallback: unsupported creationDate shape.
    return null;
  };

  // Compute the project creation time in milliseconds from project data.
  const projectCreationDateMs = getCreationDateMs(projectFile.creationDate);
  // Compute project age in milliseconds when creation time exists.
  const projectAgeMs = projectCreationDateMs ? Date.now() - projectCreationDateMs : null;
  // Mark as newly created only when transcription has not started and age is under 60 minutes.
  const isProjectNewlyCreated =
    transcriptionStatus === "notStarted" &&
    typeof projectAgeMs === "number" &&
    projectAgeMs >= 0 &&
    projectAgeMs < 60 * 60 * 1000;
  const audioChoppingBlocker =
    getAudioChoppingBlocker(transcriptionStatus, audioChoppingStatus) ||
    (nonverbalDetectionStatus === TASK_STATUS.inProgress
      ? "Wait for non-verbal detection to finish"
      : null);
  const blockers = getActionBlockers({
    statuses: {
      stemSeparation: stemSeparationStatus,
      transcription: transcriptionStatus,
      nonverbalDetection: nonverbalDetectionStatus,
      translation: translationStatus,
      audioChopping: audioChoppingStatus,
      voiceTraining: voiceTrainingStatus,
      voiceGeneration: voiceGenerationStatus,
      lipSync: lipSyncStatus,
      merging: remuxStatus,
    },
    project: projectFile,
    takesLanguage: genLang,
    languageName: getLanguageName,
  });
  // Until a requested remux starts, the status poll returns the previous
  // run's status, so the row shows the request as starting instead.
  const remuxRequest = useRemuxRequest(remuxStatus, getLanguageName);
  const shownRemuxStatus = remuxRequest.pending ? TASK_STATUS.inProgress : remuxStatus;
  const remuxBlocker = remuxRequest.blocker || blockers.remux;
  // A new video loads for up to half an hour before videoUrl changes.
  const videoIngestion = getVideoIngestionState(projectFile);
  const isVideoLoading = videoIngestion?.state === VIDEO_INGESTION_STATE.loading;
  const translatedSubtitles = projectFile.translatedSubtitles || {};
  // Offer every target language for mapping so an SRT can be uploaded for it.
  const mappingLanguages = [
    ...new Set([...(projectFile.finalLang || []), ...Object.keys(translatedSubtitles)]),
  ];
  // Disable Transcribe only while in progress or during the newly-created cooldown.
  const isTranscribeDisabled =
    transcriptionStatus === "inProgress" || isProjectNewlyCreated;
  // Projects transcribed before the service recorded its method have none.
  const transcriptionMethodLabel =
    { alignSrt: "From SRT", transcribe: "From audio" }[
      projectFile.transcriptionMethod
    ] || null;
  const fetchAllTaskStatuses = useCallback(async () => {
    const requestTaskStatus = (requestedProjectId, taskName) =>
      postWithFirebaseAuth(GET_TASKS_STATUS, {
        projectId: requestedProjectId,
        taskName,
      });
    const { statuses, progress, failures, errors } = await loadAdminActionStatuses(
      projectId,
      requestTaskStatus
    );

    // Only replace statuses whose individual requests succeeded.
    statuses.stemSeparation && setStemSeparationStatus(statuses.stemSeparation);
    statuses.transcription && setTranscriptionStatus(statuses.transcription);
    statuses.nonverbalDetection && setNonverbalDetectionStatus(statuses.nonverbalDetection);
    statuses.translation && setTranslationStatus(statuses.translation);
    statuses.voiceTraining && setVoiceTrainingStatus(statuses.voiceTraining);
    statuses.voiceGeneration && setVoiceGenerationStatus(statuses.voiceGeneration);
    statuses.audioChopping && setAudioChoppingStatus(statuses.audioChopping);
    statuses.lipSync && setLipSyncStatus(statuses.lipSync);
    statuses.merging && setRemuxStatus(statuses.merging);
    setTaskProgress((previous) => ({ ...previous, ...progress }));
    setTaskFailures((previous) => ({ ...previous, ...failures }));

    errors.forEach(({ taskName, error }) => {
      console.error(`Error fetching ${taskName} status:`, error);
    });
  }, [projectId]);



  // The URL names the project; the header and the work pages read the
  // selected project from the store, so it must follow the URL. Opening this
  // page from the sidebar does not select the project on its own.
  useEffect(() => {
    if (state.projectFile?.id !== projectId) {
      ctxDispatch({ type: "PROJECT_FILE_CLEAR" });
    }
    // Only when the URL changes; reloadProject selects the loaded project.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const reloadProject = useCallback(async () => {
    try {
      const response = await getProjectById(projectId);
      setProjectFile(response.data);
      ctxDispatch({ type: "PROJECT_FILE", payload: response.data });
      setStoredProjectFile(response.data);
    } catch (err) {
      console.log(err);
    }
  }, [ctxDispatch, projectId]);

  useEffect(() => {
    const fetchData = reloadProject;
    const checkAudioTrainingStatus = async () => {
      try {
        const response = await postWithFirebaseAuth(VOICE_TRAINING_STATUS_CHECK, {
          projectId: projectId,
        });

        setcheckAudioTrainingStatus(response.data.status);
      } catch (err) {
        console.log(err);
      }
    };

    const initialLoad = async () => {
      await fetchData();
      await checkAudioTrainingStatus();
      await fetchAllTaskStatuses(); // fetch once on mount
    };

    initialLoad();

    const interval = setInterval(() => {
      fetchAllTaskStatuses(); // refresh every 10s
    }, 10000);

    return () => clearInterval(interval);
  }, [projectId, fetchAllTaskStatuses, reloadProject]);

  // Ingestion has no task status to poll, so check the project for the new
  // video every minute while one is loading.
  useEffect(() => {
    if (!isVideoLoading) {
      return undefined;
    }
    const interval = setInterval(reloadProject, 60000);
    return () => clearInterval(interval);
  }, [isVideoLoading, reloadProject]);

  // Refresh the project once a run settles: the status chips show how its
  // output was produced, and the button prerequisites read that output
  // (dialogue stem, translation sources, trained voices).
  useEffect(() => {
    const isSettled = (status) =>
      status === TASK_STATUS.completed || status === TASK_STATUS.failed;
    const statuses = [
      stemSeparationStatus,
      transcriptionStatus,
      translationStatus,
      audioChoppingStatus,
      voiceTrainingStatus,
      voiceGenerationStatus,
    ];
    if (statuses.some(isSettled)) {
      reloadProject();
    }
  }, [
    reloadProject,
    stemSeparationStatus,
    transcriptionStatus,
    translationStatus,
    audioChoppingStatus,
    voiceTrainingStatus,
    voiceGenerationStatus,
  ]);

  async function transcribe() {
    setTranscriptionStatus(TASK_STATUS.inProgress);
    setTaskProgress((previous) => ({ ...previous, transcription: null }));
    try {
      const response = await postWithFirebaseAuth(TRANSCRIBE, {
        // The existing field accepts either a legacy URL or canonical object name.
        url: projectFile.dialogueAudioUrl,
        projectId: projectId,
      });
      console.log(response.data.taskId);
      toast.success("The movie is being transcribed");

    } catch (error) {
      toast.error("An Error has occured while transcribing");
      setTranscriptionStatus(TASK_STATUS.failed);
    }
  }

  // mode "revert" removes the model's unreviewed regions and line changes.
  async function runNonverbalDetection(mode = "detect") {
    setNonverbalDetectionStatus(TASK_STATUS.inProgress);
    setTaskProgress((previous) => ({ ...previous, nonverbalDetection: null }));
    try {
      await postWithFirebaseAuth(RUN_NONVERBAL_DETECTION, { projectId, mode });
      toast.success(
        mode === "revert"
          ? "Removing the unreviewed non-verbal sounds"
          : "Listening for coughs, laughs and other non-verbal sounds"
      );
    } catch (error) {
      toast.error(
        error.response?.data?.error || "Non-verbal detection could not start"
      );
      setNonverbalDetectionStatus(TASK_STATUS.failed);
    }
  }

  async function alignSrt() {
    setTranscriptionStatus(TASK_STATUS.inProgress);
    setTaskProgress((previous) => ({ ...previous, transcription: null }));
    try {
      // The service force-aligns the project's original SRT against the dialogue audio.
      await postWithFirebaseAuth(TRANSCRIBE, {
        projectId,
        mode: "srt",
      });
      toast.success("The original SRT is being aligned to the movie");
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          "An Error has occured while aligning the SRT"
      );
      setTranscriptionStatus(TASK_STATUS.failed);
    }
  }

  async function runStemSeparation() {
    setStemSeparationStatus(TASK_STATUS.inProgress);
    setTaskProgress((previous) => ({ ...previous, stemSeparation: null }));
    try {
      await postWithFirebaseAuth(END_TO_END_AUTOMATION, {
        projectId,
        executionMode: "stem_separation",
        stemSeparationProvider,
      });
      toast.success(
        `${STEM_SEPARATION_OPTIONS.find(
          ({ value }) => value === stemSeparationProvider
        )?.label} stem separation has been requested`
      );
      // Keep the optimistic in-progress state until polling reads the status
      // written by the stem-separation service.
    } catch (error) {
      console.error("Error requesting stem separation:", error);
      toast.error("An error occurred while requesting stem separation");
      setStemSeparationStatus(TASK_STATUS.failed);
    }
  }

  async function translate(language) {
    // helper that calls the backend
    const runTranslate = async () => {
      setTranslationStatus(TASK_STATUS.inProgress);
      setTaskProgress((previous) => ({ ...previous, translation: null }));
      try {
        await postWithFirebaseAuth(TRANSLATE, {
          projectId,
          newLanguage: language,
          mode: retranslateMode,
          userId: userInfo.id,
        });

        // const stored = localStorage.getItem("userInfo");
        // const { token } = JSON.parse(stored);

        // const res = await fetch(TRANSLATE, {
        //   method: "POST",
        //   headers: {
        //       "Content-Type": "application/json",
        //       Authorization: `Bearer ${token}`
        //   },
        //   body: JSON.stringify({
        //     projectId,
        //     newLanguage: language,
        //     mode: "hard",
        //     userId: userInfo.id,
        //   })
        // });

        // The backend only starts the translation workflow; the status poll
        // picks up progress and the final result.
        toast.info(`Translating to ${getLanguageName(language)}`);
      } catch {
        toast.error("An error occurred while translating");
        await fetchAllTaskStatuses();
      }
    };

    // Soft only fills missing translations, so it needs no confirmation.
    if (retranslateMode === "soft") {
      await runTranslate();
      return;
    }

    // Confirm before overwriting existing translations.
    const toastId = toast.info(
      () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span>
            Overwrite all will <strong>replace existing translations</strong>.
            Continue?
          </span>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              size="small"
              onClick={async () => {
                toast.dismiss(toastId);
                await runTranslate();
              }}
            >
              Confirm
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => toast.dismiss(toastId)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false }
    );
  }



  async function mapTranslation(language) {
    const runMapping = async () => {
      setTranslationStatus(TASK_STATUS.inProgress);
      setTaskProgress((previous) => ({ ...previous, translation: null }));
      try {
        await postWithFirebaseAuth(MAP_TRANSLATION, {
          projectId,
          languageCode: language,
        });
        toast.success(
          `Mapping the ${getLanguageName(language)} SRT has started`
        );
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            "An error occurred while starting the translation mapping"
        );
        setTranslationStatus(TASK_STATUS.failed);
      }
    };

    // Confirm before overwriting existing translations.
    const toastId = toast.info(
      () => (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span>
            Mapping the {getLanguageName(language)} SRT will{" "}
            <strong>overwrite existing {getLanguageName(language)} translations</strong>.
            Continue?
          </span>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              size="small"
              onClick={async () => {
                toast.dismiss(toastId);
                await runMapping();
              }}
            >
              Confirm
            </Button>

            <Button
              variant="outlined"
              size="small"
              onClick={() => toast.dismiss(toastId)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ),
      { autoClose: false, closeOnClick: false }
    );
  }

  async function audioChopping() {
    try {
      setAudioChoppingStatus(TASK_STATUS.inProgress);
      setTaskProgress((previous) => ({ ...previous, audioChopping: null }));

      // The backend accepts the job (202) and chops in the background; the
      // status poll picks up progress and the final result.
      await postWithFirebaseAuth(AUDIO_CHOPPING, { projectId });
      toast.info("Audio chopping started");
    } catch (error) {
      toast.error(error?.response?.data?.error || "Audio chopping could not start");
      await fetchAllTaskStatuses();
    }
  }



  // async function audioTraining() {
  //   try {
  //     const response = await axios.post(VOICE_TRAINING, {
  //       projectId: projectId,
  //     });
  //     toast.success("The Movie voice id has been trained and added");
  //     setcheckAudioTrainingStatus(true);
  //   } catch (error) {
  //     toast.error("An Error has occured while training the voices");
  //   }
  // }

  async function audioTraining() {
    setVoiceTrainingStatus(TASK_STATUS.inProgress);
    setTaskProgress((previous) => ({ ...previous, voiceTraining: null }));
    try {
      await postWithFirebaseAuth(VOICE_TRAINING, {
        projectId: projectId,
        use_original_audio_rating: useOriginalAudioRating,
      });
      toast.success("The Movie voice id has been trained and added");
      setcheckAudioTrainingStatus(true);
      setVoiceTrainingStatus(TASK_STATUS.completed);
    } catch (error) {
      toast.error("An Error has occured while training the voices");
      setVoiceTrainingStatus(TASK_STATUS.failed);
    }
    // Pick up the final counts and any failure reason without waiting for the poll.
    await fetchAllTaskStatuses();
  }

  async function releaseVoices() {
    try {
      await postWithFirebaseAuth(TRY_CLEAR_VOICES, {
        projectId: projectId
        // dryRun: true
      });
      toast.success("The voices have been released successfully");
      setcheckAudioTrainingStatus(true);
    } catch (error) {
      toast.error("An Error has occured while releasing the voices");
    }
  }

  // Adopt the "takes" naming convention for this workflow going forward.
  async function createTakes(language) {
    // helper that actually calls the backend
    const runGenerate = async () => {
      setVoiceGenerationStatus(TASK_STATUS.inProgress);
      setTaskProgress((previous) => ({ ...previous, voiceGeneration: null }));
      try {
        const response = await postWithFirebaseAuth(CREATE_ALL_VOCALS_AUTOMATICALLY, {
          projectId,
          language,
          soft: createTakesMode === "soft",
        });

        if (response.data?.status === false) {
          setVoiceGenerationStatus(TASK_STATUS.failed);
          toast.error("The translated takes could not be generated");
          return;
        }

        // The backend accepts the batch (202) and generates in the background;
        // the status poll picks up progress and the final result.
        toast.info(`Creating takes for ${getLanguageName(language)}`);
      } catch (error) {
        toast.error("An error has occurred while creating translated takes");
        await fetchAllTaskStatuses();
      }
    };

    // HARD mode ➜ confirmation toast
    if (createTakesMode === "hard") {
      const toastId = toast.info(
        ({ closeToast }) => (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span>
              Overwrite all will <strong>replace existing takes</strong>. Continue?
            </span>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  toast.dismiss(toastId);
                  await runGenerate();
                }}
              >
                Confirm
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => toast.dismiss(toastId)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ),
        { autoClose: false, closeOnClick: false }
      );
    } else {
      // SOFT mode – run immediately
      await runGenerate();
    }
  }


  async function syncLips(language) {
    if (!projectFile.finalLang?.includes(language)) {
      toast.error("Choose one of the project's translated languages");
      return;
    }

    setSyncSubmitting(true);
    setLipSyncStatus(TASK_STATUS.inProgress);
    try {
      await postWithFirebaseAuth(SYNC_LIPS, {
        projectId,
        languageCode: language,
      });

      toast.success(
        "Sync lips has been requested for all scenes for " +
        getLanguageName(language)
      );
    } catch (error) {
      toast.error("An Error has occured while syncing the lips");
      setLipSyncStatus(TASK_STATUS.failed);
    } finally {
      setSyncSubmitting(false);
    }
  }

  const toggleDeleteConfirmModal = () => {
    setShowDeleteConfirmModal(!showDeleteConfirmModal);
  };

  const handleDeleteProjectAPI = async (id) => {
    try {
      const response = await deleteWithFirebaseAuth(
        `${DELETE_PROJECT}/${encodeURIComponent(id)}`
      );
      console.log(response.data.msg);
      toast.success("Project has been deleted");
    } catch (error) {
      console.log(error);
      toast.error(
        error.response?.data?.error ||
          "An Error has occured while deleting the project"
      );
    }
  };

  const makeVersionStamp = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
  };

  async function reRemux(language) {
    // Recorded before the request so the button is disabled while it is sent
    // and until the job starts; see remuxRequest.js.
    remuxRequest.record(language);
    try {
      await postWithFirebaseAuth(MERGING_FULL_API, {
        projectId,
        projectName: projectFile.name,
        langCode: language,
        version: makeVersionStamp(),           // auto version name = datetime
        submitterName: userInfo?.name,
        // keep the recipients minimal; add more if you want same list as QA page
        emails: [userInfo?.email].filter(Boolean),
      });
      toast.success(`Re-remux requested for ${getLanguageName(language)}`);
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while re-remuxing");
      remuxRequest.clear();
      setRemuxStatus(TASK_STATUS.failed);
    }
  }

  // A failed task's reason is attached beneath its row; the row drops its
  // bottom border so the two read as one unit.
  const failureFor = (stateKey, status) =>
    status === TASK_STATUS.failed ? taskFailures[stateKey] : null;
  const taskRowSx = (stateKey, status) =>
    failureFor(stateKey, status) ? { "& > td": { borderBottom: "none" } } : undefined;
  const renderTaskFailure = (stateKey, status) => {
    const failure = failureFor(stateKey, status);
    return failure ? (
      <TableRow>
        <TableCell colSpan={4} sx={{ pt: 0 }}>
          <TaskFailureNotice failure={failure} />
        </TableCell>
      </TableRow>
    ) : null;
  };

  // Performance direction reads the selected project from the store, so
  // select this project before opening it, as the project menu does. No links
  // while the page is hidden from navigation ahead of deprecation.
  const performanceDirectionLinks = (
    isVisibleVideoRole("performancedirection") ? projectFile.finalLang || [] : []
  ).map(
    (language, _, languages) => ({
      to: `/work/performancedirection/${language}`,
      label:
        languages.length > 1
          ? `Performance direction · ${getLanguageName(language)}`
          : "Open performance direction",
      onClick: () => {
        ctxDispatch({ type: "PROJECT_FILE", payload: projectFile });
        setStoredProjectFile(projectFile);
      },
    })
  );

  // Chopping is language-independent, so any target language's QA Review page
  // can list the lines that failed.
  const qaReviewLanguage = projectFile.finalLang?.[0];
  const choppingFailuresLink =
    taskProgress.audioChopping?.failed > 0 && qaReviewLanguage
      ? {
          to: `/work/performancedirectionversions/${qaReviewLanguage}?attention=${CHOPPING_FAILED_ATTENTION}`,
          label: "View lines that failed to chop",
          onClick: () => {
            ctxDispatch({ type: "PROJECT_FILE", payload: projectFile });
            setStoredProjectFile(projectFile);
          },
        }
      : null;

  // Failed takes are per language, so each language with failures gets its own
  // QA Review link. Single-language runs may report no breakdown.
  const voiceGenerationProgress = taskProgress.voiceGeneration;
  const failedTakeLanguages = voiceGenerationProgress?.byLanguage?.length
    ? voiceGenerationProgress.byLanguage
        .filter((entry) => entry.failed > 0)
        .map((entry) => entry.language)
    : voiceGenerationProgress?.failed > 0 && projectFile.finalLang?.length === 1
      ? projectFile.finalLang
      : [];
  const takeFailuresLinks = failedTakeLanguages.map((language, _, languages) => ({
    to: `/work/performancedirectionversions/${language}?attention=${TAKE_FAILED_ATTENTION}`,
    label:
      languages.length > 1
        ? `Failed takes · ${getLanguageName(language)}`
        : "View lines whose takes failed",
    onClick: () => {
      ctxDispatch({ type: "PROJECT_FILE", payload: projectFile });
      setStoredProjectFile(projectFile);
    },
  }));

  return (
    <Grid item container justifyContent="center" marginBottom={24.7}>
      <Loader Load={dataLoader} />
      <ToastContainer position="top-center" />
      {/* {console.log(projectFile)} */}

      <Grid item container xs={12} px={{ xs: 2, md: 4 }} marginTop={7} direction="column">
        <Grid item sx={{ fontSize: "var(--font-size-header)", fontWeight: "600" }}>
          Actions
        </Grid>
        <Grid item>
          <Table sx={{ minWidth: 650, width: "100%", bgcolor: "white" }}>
            <colgroup>
              {ACTIONS_TABLE_COLUMN_WIDTHS.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <TableHead
              sx={{
                backgroundColor: secondaryColor,
                "& .MuiTableCell-root": { color: "var(--color-table-header-text)" },
              }}
            >
              <TableRow>
                <TableCell>Names</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Source Video</TableCell>
                <TableCell>
                  <Grid container alignItems="center" justifyContent="center">
                    <ReloadVideoButton
                      projectId={projectId}
                      projectName={projectFile.name}
                      hasVideo={Boolean(projectFile.videoUrl)}
                      blocker={blockers.reloadVideo}
                      defaultStemSeparationProvider={stemSeparationProvider}
                      onStarted={({ runId }) =>
                        setProjectFile((previous) => ({
                          ...previous,
                          videoIngestion:
                            pendingVideoIngestion(runId) || previous.videoIngestion,
                        }))
                      }
                    />
                  </Grid>
                </TableCell>
                <TableCell>
                  {isVideoLoading
                    ? "Loading new video..."
                    : videoIngestion?.state === VIDEO_INGESTION_STATE.stalled
                      ? "Last upload did not load"
                      : projectFile.videoUrl
                        ? "Loaded"
                        : "No video yet"}
                </TableCell>
                <TableCell>{describeVideoIngestion(videoIngestion) || "—"}</TableCell>
              </TableRow>
              <TableRow sx={taskRowSx("stemSeparation", stemSeparationStatus)}>
                <TableCell>Stem Separation</TableCell>
                <TableCell>
                  <Grid container alignItems="center" columnGap={2} justifyContent="center">
                    <Select
                      size="small"
                      value={stemSeparationProvider}
                      onChange={(event) => setStemSeparationProvider(event.target.value)}
                      aria-label="Stem separation service"
                    >
                      {STEM_SEPARATION_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      onClick={runStemSeparation}
                      disabled={stemSeparationStatus === TASK_STATUS.inProgress}
                    >
                      Separate
                    </Button>
                  </Grid>
                </TableCell>
                <TableCell>
                  {stemSeparationStatus === TASK_STATUS.completed
                    ? "Separated"
                    : stemSeparationStatus === TASK_STATUS.inProgress
                      ? "Separating..."
                      : stemSeparationStatus === TASK_STATUS.failed
                        ? "Failed"
                        : "Not separated yet"}
                  {/* Provided stems exist whether or not separation ran; generated
                      ones are placeholders until separation completes. */}
                  {Object.entries(projectFile.stemSources || {}).map(
                    ([audioType, source]) =>
                      stemLabels[audioType] &&
                      stemSourceLabels[source] &&
                      (source === "provided" ||
                        stemSeparationStatus === TASK_STATUS.completed) && (
                        <Chip
                          key={audioType}
                          label={`${stemLabels[audioType]} · ${stemSourceLabels[source]}`}
                          size="small"
                          variant="outlined"
                          sx={{ ...methodTagSx, mt: 0.5 }}
                        />
                      )
                  )}
                </TableCell>
                <TableCell>
                  <TaskStepsCell
                    progress={taskProgress.stemSeparation}
                    labels={
                      STEM_SEPARATION_STEP_LABELS[taskProgress.stemSeparation?.mode] ||
                      STEM_SEPARATION_STEP_LABELS.demucs
                    }
                    countNouns={STEM_SEPARATION_STEP_COUNT_NOUNS}
                  />
                </TableCell>
              </TableRow>
              {renderTaskFailure("stemSeparation", stemSeparationStatus)}
              <TableRow sx={taskRowSx("transcription", transcriptionStatus)}>
                <TableCell>Transcription</TableCell>
                <TableCell>
                  <Grid container direction="column" alignItems="center" rowGap={1.5}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={transcriptionMode}
                      onChange={(e, mode) => mode && setTranscriptionMode(mode)}
                      aria-label="Transcription method"
                      sx={translationModeToggleSx}
                    >
                      <ToggleButton value="transcribe">Transcribe</ToggleButton>
                      <ToggleButton value="srt">Align SRT</ToggleButton>
                    </ToggleButtonGroup>

                    {transcriptionMode === "transcribe" ? (
                      <GatedButton
                        onClick={() => transcribe()}
                        blocker={blockers.transcription}
                        disabled={isTranscribeDisabled}
                      >
                        Transcribe
                      </GatedButton>
                    ) : (
                      <Grid container direction="column" alignItems="center" rowGap={1.5}>
                        <Select
                          size="small"
                          value={transcriptionSrt}
                          onChange={(e) => setTranscriptionSrt(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            {projectFile.originalSubtitleObjectName
                              ? "Choose SRT"
                              : "No original SRT uploaded"}
                          </MenuItem>
                          {projectFile.originalSubtitleObjectName && (
                            <MenuItem value={projectFile.originalSubtitleObjectName}>
                              {projectFile.orginalLang
                                ? getLanguageName(projectFile.orginalLang)
                                : "Original"} (
                              {projectFile.originalSubtitleObjectName.split("/").pop()})
                            </MenuItem>
                          )}
                        </Select>
                        <Grid container alignItems="center" justifyContent="center" columnGap={2} rowGap={1}>
                          <SrtUploadButton
                            projectId={projectId}
                            role="original"
                            disabled={transcriptionStatus === TASK_STATUS.inProgress}
                            onAttached={async (copiedSubtitles) => {
                              const original = copiedSubtitles.find(
                                ({ role }) => role === "original"
                              );
                              await reloadProject();
                              if (original) {
                                setTranscriptionSrt(original.objectName);
                              }
                            }}
                          >
                            {projectFile.originalSubtitleObjectName
                              ? "Replace SRT"
                              : "Upload SRT"}
                          </SrtUploadButton>
                          <GatedButton
                            onClick={() => alignSrt()}
                            blocker={blockers.transcription}
                            disabled={!transcriptionSrt || isTranscribeDisabled}
                          >
                            Run Alignment
                          </GatedButton>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </TableCell>
                <TableCell>
                  {transcriptionStatus === "completed"
                    ? "Transcribed"
                    : transcriptionStatus === "inProgress"
                      ? "Transcribing..."
                      : transcriptionStatus === "failed"
                        ? "Failed"
                        : `Not transcribed yet${isProjectNewlyCreated ? " (Project Newly Created)" : ""}`}
                  {(transcriptionStatus === "completed" ||
                    transcriptionStatus === "failed") &&
                    transcriptionMethodLabel && (
                      <Chip
                        label={transcriptionMethodLabel}
                        size="small"
                        variant="outlined"
                        sx={methodTagSx}
                      />
                    )}
                </TableCell>
                <TableCell>
                  <TaskStepsCell
                    progress={taskProgress.transcription}
                    labels={
                      TRANSCRIPTION_STEP_LABELS[taskProgress.transcription?.mode] ||
                      TRANSCRIPTION_STEP_LABELS.transcribe
                    }
                    countNouns={TRANSCRIPTION_STEP_COUNT_NOUNS}
                  />
                </TableCell>
              </TableRow>
              {renderTaskFailure("transcription", transcriptionStatus)}
              <TableRow sx={taskRowSx("nonverbalDetection", nonverbalDetectionStatus)}>
                <TableCell>Non-verbal sounds</TableCell>
                <TableCell>
                  <Grid container direction="column" alignItems="center" rowGap={1.5}>
                    <GatedButton
                      onClick={() => runNonverbalDetection("detect")}
                      blocker={blockers.nonverbalDetection}
                    >
                      Detect
                    </GatedButton>
                    <GatedButton
                      onClick={() => runNonverbalDetection("revert")}
                      blocker={blockers.nonverbalDetection}
                    >
                      Remove unreviewed
                    </GatedButton>
                  </Grid>
                </TableCell>
                <TableCell>
                  {nonverbalDetectionStatus === "completed"
                    ? "Done; review in the SFX tab"
                    : nonverbalDetectionStatus === "inProgress"
                      ? "Detecting..."
                      : nonverbalDetectionStatus === "failed"
                        ? "Failed"
                        : "Not run yet"}
                  {mixedStemWarning(projectFile.stemSources) && (
                    <Tooltip title={mixedStemWarning(projectFile.stemSources)}>
                      <Chip
                        label="Mixed stem sources"
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ ml: 1, height: 20, mt: 0.5 }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <TaskStepsCell
                    progress={taskProgress.nonverbalDetection}
                    labels={NONVERBAL_DETECTION_STEP_LABELS}
                  />
                </TableCell>
              </TableRow>
              {renderTaskFailure("nonverbalDetection", nonverbalDetectionStatus)}
              <TableRow sx={taskRowSx("translation", translationStatus)}>
                <TableCell>Translation</TableCell>
                <TableCell>
                  <Grid container direction="column" alignItems="center" rowGap={1.5}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={translationMode}
                      onChange={(e, mode) => mode && setTranslationMode(mode)}
                      aria-label="Translation method"
                      sx={translationModeToggleSx}
                    >
                      <ToggleButton value="translate">Translate</ToggleButton>
                      <ToggleButton value="map">Map SRT</ToggleButton>
                    </ToggleButtonGroup>

                    {translationMode === "translate" ? (
                      <Grid container direction="column" alignItems="center" rowGap={1.5}>
                        <ModeCards
                          value={retranslateMode}
                          onChange={setRetranslateMode}
                          options={RETRANSLATE_MODE_OPTIONS}
                          ariaLabel="Re-translate scope"
                        />
                        <Grid container alignItems="center" columnGap={3} justifyContent="center">
                          <LangSelect
                            value={transLang}
                            onChange={setTransLang}
                            languages={projectFile.finalLang}
                            placeholder="Choose language"
                          />
                          <GatedButton
                            onClick={() => translate(transLang)}
                            blocker={blockers.translation}
                            disabled={!transLang}
                          >
                            {retranslateMode === "soft" ? "Translate missing" : "Retranslate all"}
                          </GatedButton>
                        </Grid>
                      </Grid>
                    ) : (
                      <Grid container direction="column" alignItems="center" rowGap={1.5}>
                        <Select
                          size="small"
                          value={mappingLang}
                          onChange={(e) => setMappingLang(e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            Choose language
                          </MenuItem>
                          {mappingLanguages.map((languageCode) => (
                            <MenuItem key={languageCode} value={languageCode}>
                              {getLanguageName(languageCode)} (
                              {translatedSubtitles[languageCode]
                                ? translatedSubtitles[languageCode].split("/").pop()
                                : "no SRT uploaded"}
                              )
                            </MenuItem>
                          ))}
                        </Select>
                        <Grid container alignItems="center" justifyContent="center" columnGap={2} rowGap={1}>
                          <SrtUploadButton
                            projectId={projectId}
                            role="translated"
                            languageCode={mappingLang}
                            disabled={
                              !mappingLang || translationStatus === TASK_STATUS.inProgress
                            }
                            onAttached={reloadProject}
                          >
                            {translatedSubtitles[mappingLang] ? "Replace SRT" : "Upload SRT"}
                          </SrtUploadButton>
                          <GatedButton
                            onClick={() => mapTranslation(mappingLang)}
                            blocker={blockers.translation}
                            disabled={!translatedSubtitles[mappingLang]}
                          >
                            Run Mapping
                          </GatedButton>
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </TableCell>
                <TableCell>
                  {translationStatus === "completed"
                    ? "Translated"
                    : translationStatus === "inProgress"
                      ? "Translating..."
                      : translationStatus === "failed"
                        ? "Failed"
                        : "Not translated yet"}
                  {(translationStatus === "completed" ||
                    translationStatus === "failed") &&
                    Object.entries(projectFile.translationSources || {}).map(
                      ([languageCode, source]) =>
                        translationSourceLabels[source] && (
                          <Chip
                            key={languageCode}
                            label={`${getLanguageName(languageCode)} · ${translationSourceLabels[source]}`}
                            size="small"
                            variant="outlined"
                            sx={{ ...methodTagSx, mt: 0.5 }}
                          />
                        )
                    )}
                </TableCell>
                <TableCell>
                  <TaskProgressCell
                    progress={taskProgress.translation}
                    running={translationStatus === TASK_STATUS.inProgress}
                    noun="dialogues"
                    labels={
                      TRANSLATION_PROGRESS_LABELS[taskProgress.translation?.mode] ||
                      TRANSLATION_PROGRESS_LABELS.translate
                    }
                  />
                </TableCell>
              </TableRow>
              {renderTaskFailure("translation", translationStatus)}

              <TableRow sx={taskRowSx("audioChopping", audioChoppingStatus)}>
                <TableCell>Audio Chopping</TableCell>
                <TableCell>
                  <GatedButton onClick={() => audioChopping()} blocker={audioChoppingBlocker}>
                    Chop
                  </GatedButton>
                </TableCell>
                <TableCell>
                  {audioChoppingStatus === "completed"
                    ? "Chopped"
                    : audioChoppingStatus === "inProgress"
                      ? "Chopping..."
                      : audioChoppingStatus === "failed"
                        ? "Failed"
                        : "Not chopped yet"}
                </TableCell>
                <TableCell>
                  <TaskProgressCell
                    progress={taskProgress.audioChopping}
                    running={audioChoppingStatus === TASK_STATUS.inProgress}
                    noun="segments"
                    detailsLink={choppingFailuresLink}
                  />
                </TableCell>
              </TableRow>
              {renderTaskFailure("audioChopping", audioChoppingStatus)}

              <TableRow sx={taskRowSx("voiceTraining", voiceTrainingStatus)}>
                <TableCell>Voice Training</TableCell>
                <TableCell>
                  <Grid container alignItems="center" columnGap={2} justifyContent="center">
                    <DialogueRatingSourceSelector
                      useOriginalAudioRating={useOriginalAudioRating}
                      onChange={setUseOriginalAudioRating}
                    />
                    <GatedButton
                      onClick={() => audioTraining()}
                      blocker={blockers.voiceTraining}
                      disabled={checkAudioTrainingStatus}
                    >
                      Train
                    </GatedButton>

                    <Button
                      onClick={() => releaseVoices()}
                    >
                      Release
                    </Button>
                  </Grid>
                </TableCell>
                <TableCell>
                  {voiceTrainingStatus === "completed"
                    ? "Trained"
                    : voiceTrainingStatus === "inProgress"
                      ? "Training..."
                      : voiceTrainingStatus === "failed"
                        ? "Failed"
                        : "Not trained yet"}
                </TableCell>
                <TableCell>
                  <TaskProgressCell
                    progress={withoutSkipped(taskProgress.voiceTraining)}
                    running={voiceTrainingStatus === TASK_STATUS.inProgress}
                    noun="voices"
                    labels={VOICE_TRAINING_PROGRESS_LABELS}
                    detailsLink={{
                      to: `/projects/${projectId}/voices`,
                      label: "View voices",
                    }}
                  />
                </TableCell>
              </TableRow>
              {renderTaskFailure("voiceTraining", voiceTrainingStatus)}
              <TableRow sx={taskRowSx("voiceGeneration", voiceGenerationStatus)}>
                <TableCell>Create Takes</TableCell>
                <TableCell>
                  <Grid container direction="column" alignItems="center" rowGap={1.5}>
                    <ModeCards
                      value={createTakesMode}
                      onChange={setCreateTakesMode}
                      options={CREATE_TAKES_MODE_OPTIONS}
                      ariaLabel="Create takes scope"
                    />
                    <Grid container alignItems="center" columnGap={3} justifyContent="center">
                      <LangSelect
                        value={genLang}
                        onChange={setGenLang}
                        languages={projectFile.finalLang}
                        placeholder="Choose language"
                      />
                      <GatedButton
                        onClick={() => createTakes(genLang)}
                        blocker={blockers.voiceGeneration}
                        disabled={!genLang}
                      >
                        Create Takes
                      </GatedButton>
                    </Grid>
                  </Grid>
                </TableCell>
                <TableCell>
                  {voiceGenerationStatus === "completed"
                    ? "Generated"
                    : voiceGenerationStatus === "inProgress"
                      ? "Generating..."
                      : voiceGenerationStatus === "failed"
                        ? "Failed"
                        : "Not generated yet"}
                </TableCell>
                <TableCell>
                  <TaskProgressCell
                    progress={taskProgress.voiceGeneration}
                    running={voiceGenerationStatus === TASK_STATUS.inProgress}
                    noun="takes"
                    labels={CREATE_TAKES_PROGRESS_LABELS}
                    missingLabel="missing a voice"
                    detailsLink={[...takeFailuresLinks, ...performanceDirectionLinks]}
                    languageName={getLanguageName}
                  />
                </TableCell>
              </TableRow>
              {renderTaskFailure("voiceGeneration", voiceGenerationStatus)}

              <TableRow sx={taskRowSx("lipSync", lipSyncStatus)}>
                <TableCell>Sync lips</TableCell>
                <TableCell>
                  <Grid container alignItems="center" columnGap={2} justifyContent="center">
                    <LangSelect
                      value={syncLang}
                      onChange={setSyncLang}
                      languages={projectFile.finalLang}
                      placeholder="Choose language"
                    />
                    <GatedButton
                      onClick={() => syncLips(syncLang)}
                      blocker={blockers.lipSync}
                      disabled={!syncLang || syncSubmitting}
                    >
                      {syncSubmitting ? "Starting..." : "Sync"}
                    </GatedButton>
                  </Grid>
                </TableCell>
                <TableCell>
                  {lipSyncStatus === "completed"
                    ? "Synced"
                    : lipSyncStatus === "inProgress"
                      ? "Syncing..."
                      : lipSyncStatus === "failed"
                        ? "Failed"
                        : "Not synced yet"}
                </TableCell>
                <TableCell />
              </TableRow>
              {renderTaskFailure("lipSync", lipSyncStatus)}

              <TableRow sx={taskRowSx("merging", shownRemuxStatus)}>
                <TableCell>A/V Remux &amp; QA Review</TableCell>
                <TableCell>
                  <Grid container alignItems="center" columnGap={2} justifyContent="center">
                    <LangSelect
                      value={remuxLang}
                      onChange={setRemuxLang}
                      languages={projectFile.finalLang}
                      placeholder="Choose language"
                    />
                    <GatedButton
                      onClick={() => reRemux(remuxLang)}
                      blocker={remuxBlocker}
                      disabled={!remuxLang}
                    >
                      Re-remux
                    </GatedButton>
                  </Grid>
                </TableCell>
                <TableCell>
                  {remuxRequest.pending
                    ? "Starting..."
                    : remuxStatus === "completed"
                      ? "Remuxed"
                      : remuxStatus === "inProgress"
                        ? "Re-remuxing..."
                        : remuxStatus === "failed"
                          ? "Failed"
                          : "Not remuxed yet"}
                </TableCell>
                <TableCell />
              </TableRow>
              {renderTaskFailure("merging", shownRemuxStatus)}

            </TableBody>
          </Table>
        </Grid>
        <Grid item>
          <QAReviewPanel projectId={projectId} />
        </Grid>
        <Grid item container marginTop={12} direction="column">
          <Button
            size="small"
            color="error"
            variant="contained"
            onClick={() => {
              toggleDeleteConfirmModal();
              setDeletedProjectName(projectFile.name);
              setDeletedProjectId(projectId);
              // handleDeleteProjectAPI(project.id);
            }}
          >
            Delete
          </Button>
          {showDeleteConfirmModal && (
            <DeleteConfirmationModal
              projectName={deletedProjectName}
              projectId={deletedProjectId}
              onClose={toggleDeleteConfirmModal}
              handleDeleteProjectAPI={handleDeleteProjectAPI}
            // handleRemoveProject={handleRemoveProject}
            />
          )}
        </Grid>
      </Grid >
    </Grid >
  );
};

export default ActionsScreen;
